import React, { useState, useEffect } from 'react';
import { CheckCircle, Circle, ArrowRight, ArrowLeft, ExternalLink, Copy, Zap, TrendingUp, Globe, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const OnboardingFlow = ({ onComplete }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  // Carregar dados salvos do localStorage
  const loadSavedData = () => {
    try {
      const saved = localStorage.getItem('monetizemaster_onboarding');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Erro ao carregar dados salvos:', error);
    }
    return {
      clickbank: { apiKey: '', developerKey: '' },
      hotmart: { clientId: '', clientSecret: '' },
      monetizze: { token: '' },
      amazon: { accessKey: '', secretKey: '', associateTag: '', region: 'US' },
      goals: {
        monthlyTarget: '',
        niche: '',
        experience: ''
      }
    };
  };

  const [formData, setFormData] = useState(loadSavedData());
  const [testResults, setTestResults] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [skippedSteps, setSkippedSteps] = useState([]);
  const [showSavedNotice, setShowSavedNotice] = useState(false);

  // Mostrar aviso se dados foram carregados
  useEffect(() => {
    const hasSavedData = localStorage.getItem('monetizemaster_onboarding');
    if (hasSavedData) {
      setShowSavedNotice(true);
      setTimeout(() => setShowSavedNotice(false), 5000);
    }
  }, []);

  const steps = [
    {
      id: 'welcome',
      title: 'Bem-vindo ao MonetizeMaster! 🎉',
      description: 'Vamos configurar tudo para você começar a ganhar suas primeiras comissões',
      component: 'WelcomeStep'
    },
    {
      id: 'goals',
      title: 'Defina Seus Objetivos 🎯',
      description: 'Conte-nos sobre suas metas para personalizarmos sua experiência',
      component: 'GoalsStep'
    },
    {
      id: 'clickbank',
      title: 'ClickBank API 🏦',
      description: 'Configure sua conta ClickBank para produtos internacionais',
      component: 'ClickBankStep'
    },
    {
      id: 'hotmart',
      title: 'Hotmart API 🔥',
      description: 'Conecte com a maior plataforma de produtos digitais do Brasil',
      component: 'HotmartStep'
    },
    {
      id: 'monetizze',
      title: 'Monetizze API 💎',
      description: 'Acesse infoprodutos brasileiros de alta conversão',
      component: 'MonetizzeStep'
    },
    {
      id: 'amazon',
      title: 'Amazon Associates 📦',
      description: 'Configure para produtos físicos com alta conversão',
      component: 'AmazonStep'
    },
    {
      id: 'test',
      title: 'Testar Conexões 🔧',
      description: 'Vamos verificar se tudo está funcionando corretamente',
      component: 'TestStep'
    },
    {
      id: 'success',
      title: 'Tudo Pronto! 🚀',
      description: 'Seu sistema está configurado e pronto para gerar comissões',
      component: 'SuccessStep'
    }
  ];

  const markStepCompleted = (stepId) => {
    if (!completedSteps.includes(stepId)) {
      setCompletedSteps([...completedSteps, stepId]);
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      markStepCompleted(steps[currentStep].id);
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const updateFormData = (section, data) => {
    const newData = {
      ...formData,
      [section]: { ...formData[section], ...data }
    };
    setFormData(newData);
    
    // Salvar automaticamente no localStorage
    try {
      localStorage.setItem('monetizemaster_onboarding', JSON.stringify(newData));
    } catch (error) {
      console.error('Erro ao salvar dados:', error);
    }
  };

  const skipStep = (stepId) => {
    if (!skippedSteps.includes(stepId)) {
      setSkippedSteps([...skippedSteps, stepId]);
    }
    markStepCompleted(stepId);
    nextStep();
  };

  const testConnection = async (platform) => {
    // Verificar se a API foi pulada
    if (skippedSteps.includes(platform)) {
      setTestResults(prev => ({ ...prev, [platform]: 'skipped' }));
      return;
    }
    
    // Verificar se as credenciais foram preenchidas
    const hasCredentials = () => {
      switch(platform) {
        case 'clickbank':
          return formData.clickbank.apiKey;
        case 'hotmart':
          return formData.hotmart.clientId && formData.hotmart.clientSecret;
        case 'monetizze':
          return formData.monetizze.token;
        case 'amazon':
          return formData.amazon.accessKey && formData.amazon.secretKey && formData.amazon.associateTag;
        default:
          return false;
      }
    };
    
    if (!hasCredentials()) {
      setTestResults(prev => ({ ...prev, [platform]: 'not_configured' }));
      return;
    }
    
    setIsLoading(true);
    try {
      // Simular teste de API (substitua pela lógica real)
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/test-connection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform, credentials: formData[platform] })
      });
      
      const result = await response.json();
      setTestResults(prev => ({ ...prev, [platform]: result.success ? 'success' : 'error' }));
      
      if (result.success) {
        markStepCompleted(platform);
      }
    } catch (error) {
      setTestResults(prev => ({ ...prev, [platform]: 'error' }));
    }
    setIsLoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const renderWelcomeStep = () => (
    <div className="text-center space-y-6">
      <div className="text-6xl mb-6">🎉</div>
      <h2 className="text-3xl font-bold text-white mb-4">
        Bem-vindo ao <span className="text-yellow-400">MonetizeMaster</span>!
      </h2>
      <p className="text-gray-300 text-lg max-w-2xl mx-auto">
        Você está prestes a transformar sua vida financeira! Em alguns minutos, 
        seu sistema estará configurado e gerando suas primeiras comissões automaticamente.
      </p>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
          <Zap className="h-8 w-8 text-blue-400 mx-auto mb-2" />
          <h3 className="text-white font-bold mb-1">15 Minutos</h3>
          <p className="text-gray-400 text-sm">Setup completo</p>
        </div>
        <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
          <TrendingUp className="h-8 w-8 text-green-400 mx-auto mb-2" />
          <h3 className="text-white font-bold mb-1">9 Plataformas</h3>
          <p className="text-gray-400 text-sm">Fontes de renda</p>
        </div>
        <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
          <Globe className="h-8 w-8 text-purple-400 mx-auto mb-2" />
          <h3 className="text-white font-bold mb-1">195+ Países</h3>
          <p className="text-gray-400 text-sm">Alcance global</p>
        </div>
        <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
          <DollarSign className="h-8 w-8 text-yellow-400 mx-auto mb-2" />
          <h3 className="text-white font-bold mb-1">24/7</h3>
          <p className="text-gray-400 text-sm">Automação total</p>
        </div>
      </div>
      
      <div className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 border border-green-500/30 rounded-lg p-6 mt-8">
        <h3 className="text-green-400 font-bold mb-2">🎯 Meta deste onboarding:</h3>
        <p className="text-gray-300">
          Ao final, você terá um sistema completo gerando comissões reais das 4 maiores 
          plataformas de afiliados do mundo!
        </p>
      </div>
    </div>
  );

  const renderGoalsStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-4">🎯 Defina Seus Objetivos</h2>
        <p className="text-gray-300 text-lg">
          Essas informações nos ajudam a personalizar sua experiência
        </p>
      </div>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white/5 border border-white/10 rounded-lg p-6">
          <h3 className="text-white font-bold mb-4">💰 Meta Mensal</h3>
          <select
            value={formData.goals.monthlyTarget}
            onChange={(e) => updateFormData('goals', { monthlyTarget: e.target.value })}
            className="w-full bg-gray-800 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-yellow-500 appearance-none cursor-pointer hover:bg-gray-700"
            style={{color: '#ffffff'}}
          >
            <option value="" style={{color: '#999'}}>Selecione suas metas</option>
            <option value="500-1000" style={{color: '#ffffff'}}>💵 R$ 500 - R$ 1.000 (Iniciante)</option>
            <option value="1000-3000" style={{color: '#ffffff'}}>💰 R$ 1.000 - R$ 3.000 (Intermediário)</option>
            <option value="3000-5000" style={{color: '#ffffff'}}>💸 R$ 3.000 - R$ 5.000 (Avançado)</option>
            <option value="5000-10000" style={{color: '#ffffff'}}>💎 R$ 5.000 - R$ 10.000 (Profissional)</option>
            <option value="10000-20000" style={{color: '#ffffff'}}>🏆 R$ 10.000 - R$ 20.000 (Expert)</option>
            <option value="20000+" style={{color: '#ffffff'}}>🚀 R$ 20.000+ (Master)</option>
          </select>
        </div>
        
        <div className="bg-white/5 border border-white/10 rounded-lg p-6">
          <h3 className="text-white font-bold mb-4">🎨 Escolha seu Nicho</h3>
          <select
            value={formData.goals.niche}
            onChange={(e) => updateFormData('goals', { niche: e.target.value })}
            className="w-full bg-gray-800 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-yellow-500 appearance-none cursor-pointer hover:bg-gray-700"
            style={{color: '#ffffff'}}
          >
            <option value="" style={{color: '#999'}}>Escolha um nicho</option>
            <option value="marketing-digital" style={{color: '#ffffff'}}>📱 Marketing Digital</option>
            <option value="saude-fitness" style={{color: '#ffffff'}}>💪 Saúde & Fitness</option>
            <option value="emagrecimento" style={{color: '#ffffff'}}>🏃 Emagrecimento</option>
            <option value="tecnologia" style={{color: '#ffffff'}}>💻 Tecnologia</option>
            <option value="educacao" style={{color: '#ffffff'}}>📚 Educação</option>
            <option value="desenvolvimento-pessoal" style={{color: '#ffffff'}}>🧠 Desenvolvimento Pessoal</option>
            <option value="financas" style={{color: '#ffffff'}}>💰 Finanças & Investimentos</option>
            <option value="casa-jardim" style={{color: '#ffffff'}}>🏠 Casa & Jardim</option>
            <option value="moda-beleza" style={{color: '#ffffff'}}>👗 Moda & Beleza</option>
            <option value="culinaria" style={{color: '#ffffff'}}>🍳 Culinária</option>
            <option value="games" style={{color: '#ffffff'}}>🎮 Games & E-sports</option>
            <option value="esportes" style={{color: '#ffffff'}}>⚽ Esportes</option>
            <option value="viagens" style={{color: '#ffffff'}}>✈️ Viagens & Turismo</option>
            <option value="pets" style={{color: '#ffffff'}}>🐶 Pets</option>
            <option value="musica" style={{color: '#ffffff'}}>🎵 Música</option>
            <option value="fotografia" style={{color: '#ffffff'}}>📷 Fotografia</option>
            <option value="relacionamentos" style={{color: '#ffffff'}}>❤️ Relacionamentos</option>
            <option value="maternidade" style={{color: '#ffffff'}}>👶 Maternidade</option>
            <option value="automoveis" style={{color: '#ffffff'}}>🚗 Automóveis</option>
            <option value="diversos" style={{color: '#ffffff'}}>🌟 Diversos</option>
          </select>
        </div>
        
        <div className="bg-white/5 border border-white/10 rounded-lg p-6">
          <h3 className="text-white font-bold mb-4">📊 Seu Nível de Experiência</h3>
          <select
            value={formData.goals.experience}
            onChange={(e) => updateFormData('goals', { experience: e.target.value })}
            className="w-full bg-gray-800 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-yellow-500 appearance-none cursor-pointer hover:bg-gray-700"
            style={{color: '#ffffff'}}
          >
            <option value="" style={{color: '#999'}}>Selecione seu nível</option>
            <option value="completo-iniciante" style={{color: '#ffffff'}}>🌱 Completo Iniciante (Nunca fiz marketing de afiliados)</option>
            <option value="iniciante" style={{color: '#ffffff'}}>🔰 Iniciante (Até 6 meses de experiência)</option>
            <option value="intermediario" style={{color: '#ffffff'}}>📈 Intermediário (6 meses a 2 anos)</option>
            <option value="avancado" style={{color: '#ffffff'}}>🚀 Avançado (Mais de 2 anos)</option>
            <option value="profissional" style={{color: '#ffffff'}}>💎 Profissional (Já vivo disso)</option>
          </select>
        </div>
      </div>
      
      <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-6 mt-8">
        <h3 className="text-blue-400 font-bold mb-2">💡 Dica Personalizada:</h3>
        <p className="text-gray-300">
          {formData.goals.monthlyTarget === '500-1000' && 
            '🌱 Perfeito para começar! Foque em 1-2 produtos por plataforma. Use bastante a IA de conteúdo e publique 2-3x por dia.'}
          {formData.goals.monthlyTarget === '1000-3000' && 
            '💪 Ótima meta inicial! Foque em 2-3 produtos por plataforma. Teste diferentes nichos e acompanhe as métricas.'}
          {formData.goals.monthlyTarget === '3000-5000' && 
            '🚀 Meta intermediária! Diversifique para 5-8 produtos, automatize postagens e invista R$200-500 em tráfego pago.'}
          {formData.goals.monthlyTarget === '5000-10000' && 
            '💎 Meta avançada! Configure automação completa, múltiplas fontes de tráfego e considere contratar freelancers.'}
          {(formData.goals.monthlyTarget === '10000-20000' || formData.goals.monthlyTarget === '20000+') && 
            '🏆 Meta profissional! Implemente funis completos, time de conteúdo e parcerias com influencers.'}
          {!formData.goals.monthlyTarget && 
            'Selecione sua meta mensal acima para receber dicas personalizadas baseadas no seu objetivo!'}
        </p>
        {formData.goals.niche && (
          <p className="text-gray-400 text-sm mt-3">
            <strong>Sobre {formData.goals.niche}:</strong> {' '}
            {formData.goals.niche === 'marketing-digital' && 'Nicho lucrativo! Público qualificado e produtos de alto ticket.'}
            {formData.goals.niche === 'saude-fitness' && 'Nicho evergreen! Alta demanda constante e ótimas comissões.'}
            {formData.goals.niche === 'emagrecimento' && 'Nicho muito quente! Alto volume de buscas e conversões.'}
            {formData.goals.niche === 'financas' && 'Nicho premium! Público com poder aquisitivo elevado.'}
          </p>
        )}
        {formData.goals.experience && (
          <p className="text-gray-400 text-sm mt-2">
            <strong>Para {formData.goals.experience}:</strong> {' '}
            {(formData.goals.experience === 'completo-iniciante' || formData.goals.experience === 'iniciante') && 'Comece com produtos simples e aumente gradualmente. Use nossos templates de conteúdo!'}
            {formData.goals.experience === 'intermediario' && 'Hora de escalar! Teste tráfego pago e automatize processos.'}
            {(formData.goals.experience === 'avancado' || formData.goals.experience === 'profissional') && 'Foque em otimização de conversão e diversificação de fontes.'}
          </p>
        )}
      </div>
    </div>
  );

  const renderAPIStep = (platform, config) => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-4">
          {config.icon} {config.title}
        </h2>
        <p className="text-gray-300 text-lg">{config.description}</p>
      </div>
      
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Instructions */}
        <div className="bg-white/5 border border-white/10 rounded-lg p-6">
          <h3 className="text-white font-bold mb-4">📋 Como Obter as Chaves:</h3>
          <ol className="space-y-3 text-gray-300">
            {config.instructions.map((instruction, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="bg-yellow-500 text-black w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">
                  {index + 1}
                </span>
                <div>
                  {instruction.includes('http') ? (
                    <span>
                      {instruction.split('http')[0]}
                      <a 
                        href={`http${instruction.split('http')[1]}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:underline inline-flex items-center gap-1"
                      >
                        {`http${instruction.split('http')[1]}`}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </span>
                  ) : (
                    instruction
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
        
        {/* Form */}
        <div className="bg-white/5 border border-white/10 rounded-lg p-6">
          <h3 className="text-white font-bold mb-4">🔑 Configuração:</h3>
          <div className="space-y-4">
            {config.fields.map((field) => (
              <div key={field.key}>
                <label className="block text-gray-300 mb-2">
                  {field.label}
                  {field.required && <span className="text-red-400 ml-1">*</span>}
                </label>
                {field.type === 'select' ? (
                  <select
                    value={formData[platform][field.key] || ''}
                    onChange={(e) => updateFormData(platform, { [field.key]: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-yellow-500"
                    required={field.required}
                  >
                    <option value="">Selecione...</option>
                    {field.options.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                ) : (
                  <div className="relative">
                    <input
                      type={field.type || 'text'}
                      placeholder={field.placeholder}
                      value={formData[platform][field.key] || ''}
                      onChange={(e) => updateFormData(platform, { [field.key]: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 pr-10"
                      required={field.required}
                    />
                    <button
                      type="button"
                      onClick={() => copyToClipboard(formData[platform][field.key] || '')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                )}
                {field.help && (
                  <p className="text-gray-400 text-sm mt-1">{field.help}</p>
                )}
              </div>
            ))}
          </div>
          
          <div className="space-y-3 mt-6">
            <button
              onClick={() => testConnection(platform)}
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 rounded-lg font-bold hover:from-green-600 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Testando...' : '🔧 Testar Conexão'}
            </button>
            
            <button
              onClick={() => skipStep(platform)}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white py-2 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
            >
              ⏭️ Pular esta etapa (configurar depois)
            </button>
            
            <p className="text-gray-400 text-xs text-center">
              Você pode configurar esta API depois no menu "Setup APIs"
            </p>
          </div>
          
          {testResults[platform] !== undefined && (
            <div className={`mt-4 p-3 rounded-lg ${testResults[platform] ? 'bg-green-900/20 border border-green-500/30' : 'bg-red-900/20 border border-red-500/30'}`}>
              <p className={`font-bold ${testResults[platform] ? 'text-green-400' : 'text-red-400'}`}>
                {testResults[platform] ? '✅ Conexão bem-sucedida!' : '❌ Erro na conexão'}
              </p>
              <p className="text-gray-300 text-sm mt-1">
                {testResults[platform] 
                  ? 'Suas credenciais estão corretas e funcionando.'
                  : 'Verifique suas credenciais e tente novamente.'}
              </p>
            </div>
          )}
        </div>
      </div>
      
      <div className={`p-6 rounded-lg ${config.bgColor}`}>
        <h3 className={`font-bold mb-2 ${config.textColor}`}>
          💰 Potencial de Ganhos:
        </h3>
        <p className="text-gray-300">{config.potential}</p>
      </div>
    </div>
  );

  const apiConfigs = {
    clickbank: {
      icon: '🏦',
      title: 'ClickBank API',
      description: 'Configure sua conta ClickBank para produtos digitais internacionais',
      bgColor: 'bg-blue-900/20 border border-blue-500/30',
      textColor: 'text-blue-400',
      potential: 'Comissões de R$ 150-500 por venda | Cookie de 60 dias | Produtos de alto valor',
      instructions: [
        'Acesse https://accounts.clickbank.com',
        'Faça login na sua conta ClickBank',
        'Vá em "Settings" → "My Site"',
        'Copie sua "API Key" e "Developer Key"'
      ],
      fields: [
        {
          key: 'apiKey',
          label: 'API Key',
          placeholder: 'Sua API Key do ClickBank',
          required: true,
          help: 'Encontrada em Settings → My Site'
        },
        {
          key: 'developerKey',
          label: 'Developer Key',
          placeholder: 'Sua Developer Key (opcional)',
          required: false,
          help: 'Para funcionalidades avançadas'
        }
      ]
    },
    hotmart: {
      icon: '🔥',
      title: 'Hotmart API',
      description: 'Conecte com a maior plataforma de produtos digitais do Brasil',
      bgColor: 'bg-orange-900/20 border border-orange-500/30',
      textColor: 'text-orange-400',
      potential: 'Comissões de 20-70% | PIX semanal automático | Foco no mercado brasileiro',
      instructions: [
        'Acesse https://developers.hotmart.com',
        'Faça login com sua conta Hotmart',
        'Vá em "Minhas Aplicações" → "Nova Aplicação"',
        'Copie o "Client ID" e "Client Secret"'
      ],
      fields: [
        {
          key: 'clientId',
          label: 'Client ID',
          placeholder: 'Seu Client ID da Hotmart',
          required: true
        },
        {
          key: 'clientSecret',
          label: 'Client Secret',
          placeholder: 'Seu Client Secret da Hotmart',
          required: true,
          type: 'password'
        }
      ]
    },
    monetizze: {
      icon: '💎',
      title: 'Monetizze API',
      description: 'Acesse infoprodutos brasileiros de alta conversão',
      bgColor: 'bg-purple-900/20 border border-purple-500/30',
      textColor: 'text-purple-400',
      potential: 'Comissões de 30-80% | Especialidade em infoprodutos | Mercado brasileiro',
      instructions: [
        'Acesse https://app.monetizze.com.br',
        'Faça login na sua conta',
        'Vá em "Integrações" → "API"',
        'Copie seu "Token de API"'
      ],
      fields: [
        {
          key: 'token',
          label: 'Token/Chave de API',
          placeholder: 'Seu Token da Monetizze',
          required: true,
          help: 'Apenas 1 campo necessário!'
        }
      ]
    },
    amazon: {
      icon: '📦',
      title: 'Amazon Associates',
      description: 'Configure para produtos físicos com alta conversão',
      bgColor: 'bg-yellow-900/20 border border-yellow-500/30',
      textColor: 'text-yellow-400',
      potential: 'Comissões de 1-10% (alto volume) | Conversão muito alta | Marca confiável',
      instructions: [
        'Acesse https://affiliate-program.amazon.com',
        'Cadastre-se no programa de afiliados',
        'Acesse "Ferramentas" → "Product Advertising API"',
        'Gere suas credenciais de acesso'
      ],
      fields: [
        {
          key: 'accessKey',
          label: 'Access Key',
          placeholder: 'Sua Access Key da Amazon',
          required: true
        },
        {
          key: 'secretKey',
          label: 'Secret Key',
          placeholder: 'Sua Secret Key da Amazon',
          required: true,
          type: 'password'
        },
        {
          key: 'associateTag',
          label: 'Associate Tag',
          placeholder: 'Sua Associate Tag',
          required: true,
          help: 'Também conhecida como Tracking ID'
        },
        {
          key: 'region',
          label: 'Região',
          type: 'select',
          required: true,
          options: [
            { value: 'US', label: 'Estados Unidos (US)' },
            { value: 'BR', label: 'Brasil (BR)' },
            { value: 'CA', label: 'Canadá (CA)' },
            { value: 'UK', label: 'Reino Unido (UK)' },
            { value: 'DE', label: 'Alemanha (DE)' },
            { value: 'FR', label: 'França (FR)' }
          ]
        }
      ]
    }
  };

  const renderTestStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-4">🔧 Testar Todas as Conexões</h2>
        <p className="text-gray-300 text-lg">
          Vamos verificar se todas as suas APIs estão funcionando corretamente
        </p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6">
        {Object.entries(apiConfigs).map(([platform, config]) => {
          const status = testResults[platform];
          
          return (
            <div key={platform} className={`p-6 rounded-lg ${config.bgColor} border-2 ${
              status === 'success' ? 'border-green-500' : 
              status === 'error' ? 'border-red-500' : 
              status === 'skipped' ? 'border-orange-500' : 
              status === 'not_configured' ? 'border-yellow-500' : 
              'border-transparent'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold flex items-center gap-2">
                  <span className="text-2xl">{config.icon}</span>
                  {config.title}
                </h3>
                <div className="flex items-center gap-2">
                  {status === 'success' ? (
                    <>
                      <CheckCircle className="h-6 w-6 text-green-400" />
                      <span className="text-green-400 text-sm font-bold">OK</span>
                    </>
                  ) : status === 'error' ? (
                    <>
                      <Circle className="h-6 w-6 text-red-400" />
                      <span className="text-red-400 text-sm font-bold">Erro</span>
                    </>
                  ) : status === 'skipped' ? (
                    <>
                      <span className="text-2xl">⏭️</span>
                      <span className="text-orange-400 text-sm font-bold">Pulado</span>
                    </>
                  ) : status === 'not_configured' ? (
                    <>
                      <Circle className="h-6 w-6 text-yellow-400" />
                      <span className="text-yellow-400 text-sm font-bold">Não Configurado</span>
                    </>
                  ) : (
                    <>
                      <Circle className="h-6 w-6 text-gray-400" />
                      <span className="text-gray-400 text-sm">Aguardando</span>
                    </>
                  )}
                </div>
              </div>
              
              <button
                onClick={() => testConnection(platform)}
                disabled={isLoading || status === 'skipped'}
                className={`w-full py-2 rounded-lg font-medium transition-all disabled:opacity-50 ${
                  status === 'skipped' 
                    ? 'bg-orange-500/20 text-orange-400 cursor-not-allowed'
                    : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
              >
                {status === 'skipped' ? '⏭️ Pulado' : `Testar ${config.title}`}
              </button>
              
              {status === 'not_configured' && (
                <p className="text-yellow-300 text-xs mt-2 text-center">
                  ⚠️ Credenciais não preenchidas
                </p>
              )}
            </div>
          );
        })}
      </div>
      
      <div className="text-center space-y-4">
        <button
          onClick={async () => {
            setIsLoading(true);
            // Testar todas as conexões
            for (const platform of Object.keys(apiConfigs)) {
              await testConnection(platform);
              // Pequena pausa entre os testes para não sobrecarregar
              await new Promise(resolve => setTimeout(resolve, 500));
            }
            setIsLoading(false);
          }}
          disabled={isLoading}
          className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-lg font-bold hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50"
        >
          {isLoading ? '🔄 Testando todas as APIs...' : '🚀 Testar Todas as Conexões'}
        </button>
        
        {/* Resumo dos resultados */}
        {Object.keys(testResults).length > 0 && (
          <div className="bg-white/5 border border-white/10 rounded-lg p-4 inline-block">
            <p className="text-white font-bold mb-2">📊 Resumo dos Testes:</p>
            <div className="flex gap-4 text-sm">
              <span className="text-green-400">
                ✅ Sucesso: {Object.values(testResults).filter(r => r === 'success').length}
              </span>
              <span className="text-yellow-400">
                ⚠️ Não Configurado: {Object.values(testResults).filter(r => r === 'not_configured').length}
              </span>
              <span className="text-orange-400">
                ⏭️ Pulado: {Object.values(testResults).filter(r => r === 'skipped').length}
              </span>
              <span className="text-red-400">
                ❌ Erro: {Object.values(testResults).filter(r => r === 'error').length}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderSuccessStep = () => {
    // Filtrar apenas plataformas que foram testadas com SUCESSO (não puladas ou não configuradas)
    const connectedPlatforms = Object.keys(testResults).filter(platform => testResults[platform] === 'success');
    
    return (
      <div className="text-center space-y-6">
        <div className="text-6xl mb-6">🚀</div>
        <h2 className="text-3xl font-bold text-white mb-4">
          <span className="text-green-400">Parabéns!</span> Tudo Está Configurado!
        </h2>
        <p className="text-gray-300 text-lg max-w-2xl mx-auto">
          Seu sistema MonetizeMaster está 100% operacional e pronto para gerar suas primeiras comissões!
        </p>
        
        <div className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 border border-green-500/30 rounded-lg p-6 max-w-2xl mx-auto">
          <h3 className="text-green-400 font-bold mb-4">✅ Plataformas Conectadas no Onboarding:</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {connectedPlatforms.length > 0 ? (
              connectedPlatforms.map(platform => (
                <div key={platform} className="flex items-center gap-2 text-white">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                  <span>{apiConfigs[platform].title}</span>
                </div>
              ))
            ) : (
              <div className="col-span-2 text-gray-400 text-sm">
                Nenhuma plataforma foi configurada no onboarding. Você pulou todas as etapas.
              </div>
            )}
          </div>
        </div>
        
        {/* New Platforms Available */}
        <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-6 max-w-2xl mx-auto">
          <h3 className="text-purple-400 font-bold mb-3">🆕 Mais Plataformas Disponíveis!</h3>
          <p className="text-gray-300 text-sm mb-4">
            Agora você pode conectar mais 4 plataformas de afiliados pelo menu <strong>"🔧 Setup APIs"</strong>:
          </p>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <div className="bg-white/5 rounded-lg p-3">
              <div className="flex items-center gap-2 text-white mb-1">
                <span className="text-lg">🛒</span>
                <strong>Shopee Afiliados</strong>
              </div>
              <p className="text-gray-400 text-xs">E-commerce Ásia e América Latina</p>
            </div>
            <div className="bg-white/5 rounded-lg p-3">
              <div className="flex items-center gap-2 text-white mb-1">
                <span className="text-lg">🏪</span>
                <strong>Magazine Você (Magalu)</strong>
              </div>
              <p className="text-gray-400 text-xs">Maior varejista brasileiro</p>
            </div>
            <div className="bg-white/5 rounded-lg p-3">
              <div className="flex items-center gap-2 text-white mb-1">
                <span className="text-lg">💛</span>
                <strong>Mercado Livre</strong>
              </div>
              <p className="text-gray-400 text-xs">Maior marketplace da América Latina</p>
            </div>
            <div className="bg-white/5 rounded-lg p-3">
              <div className="flex items-center gap-2 text-white mb-1">
                <span className="text-lg">🌐</span>
                <strong>Awin</strong>
              </div>
              <p className="text-gray-400 text-xs">Rede global - Marcas internacionais</p>
            </div>
          </div>
          <div className="mt-4 text-center space-y-3">
            <a 
              href="/setup" 
              className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-8 rounded-lg transition-colors"
            >
              Configurar Agora →
            </a>
            <div>
              <a 
                href="/international-guide" 
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors text-sm"
              >
                🌍 Ver Guia de Marketing Internacional
              </a>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 max-w-4xl mx-auto mb-6">
          <p className="text-blue-300 text-sm text-center">
            💡 <strong>Dica:</strong> Você pode acessar todas as funcionalidades a qualquer momento pelo <strong>menu superior</strong>. Não precisa voltar aqui!
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="bg-white/5 border border-white/10 rounded-lg p-6">
            <h3 className="text-white font-bold mb-2">🔗 Próximo Passo</h3>
            <p className="text-gray-300 text-sm mb-4">Gere seus primeiros links de afiliado</p>
            <button 
              onClick={() => navigate('/links')}
              className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Ir para Links
            </button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6">
            <h3 className="text-white font-bold mb-2">🤖 IA de Conteúdo</h3>
            <p className="text-gray-300 text-sm mb-4">Crie conteúdo que converte</p>
            <button 
              onClick={() => navigate('/content-generator')}
              className="w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              Gerar Conteúdo
            </button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6">
            <h3 className="text-white font-bold mb-2">📊 Dashboard</h3>
            <p className="text-gray-300 text-sm mb-4">Acompanhe suas comissões</p>
            <button 
              onClick={() => navigate('/dashboard')}
              className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              Ver Dashboard
            </button>
          </div>
        </div>
        
        <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-6 max-w-2xl mx-auto">
          <h3 className="text-yellow-400 font-bold mb-2">🎯 Expectativas Realistas:</h3>
          <ul className="text-gray-300 text-left space-y-1">
            <li>• <strong>Próximas 24-48h:</strong> Primeiros cliques nos seus links</li>
            <li>• <strong>Semana 1-2:</strong> Primeira comissão (R$ 50-200)</li>
            <li>• <strong>Mês 1:</strong> R$ 500-1.500</li>
            <li>• <strong>Mês 3+:</strong> R$ 2.000-5.000+</li>
          </ul>
        </div>
        
        <button
          onClick={() => {
            if (onComplete) onComplete();
            navigate('/dashboard');
          }}
          className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-12 py-4 rounded-lg font-bold text-lg hover:from-yellow-600 hover:to-orange-700 transition-all shadow-2xl"
        >
          🚀 COMEÇAR A GANHAR AGORA!
        </button>
      </div>
    );
  };

  const renderCurrentStep = () => {
    const step = steps[currentStep];
    
    switch (step.component) {
      case 'WelcomeStep': return renderWelcomeStep();
      case 'GoalsStep': return renderGoalsStep();
      case 'ClickBankStep': return renderAPIStep('clickbank', apiConfigs.clickbank);
      case 'HotmartStep': return renderAPIStep('hotmart', apiConfigs.hotmart);
      case 'MonetizzeStep': return renderAPIStep('monetizze', apiConfigs.monetizze);
      case 'AmazonStep': return renderAPIStep('amazon', apiConfigs.amazon);
      case 'TestStep': return renderTestStep();
      case 'SuccessStep': return renderSuccessStep();
      default: return <div>Carregando...</div>;
    }
  };

  const canProceed = () => {
    const step = steps[currentStep];
    
    // Se a etapa foi pulada, pode prosseguir
    if (skippedSteps.includes(step.id)) {
      return true;
    }
    
    switch (step.id) {
      case 'goals':
        return formData.goals.monthlyTarget && formData.goals.niche && formData.goals.experience;
      case 'clickbank':
        return formData.clickbank.apiKey || skippedSteps.includes('clickbank');
      case 'hotmart':
        return (formData.hotmart.clientId && formData.hotmart.clientSecret) || skippedSteps.includes('hotmart');
      case 'monetizze':
        return formData.monetizze.token || skippedSteps.includes('monetizze');
      case 'amazon':
        return (formData.amazon.accessKey && formData.amazon.secretKey && formData.amazon.associateTag && formData.amazon.region) || skippedSteps.includes('amazon');
      case 'test':
        // Na etapa de teste, permitir prosseguir se pelo menos uma API foi configurada com sucesso
        const hasAnySuccess = Object.values(testResults).some(result => result === 'success');
        const allSkipped = ['clickbank', 'hotmart', 'monetizze', 'amazon'].every(api => skippedSteps.includes(api));
        
        // Pode prosseguir se: tem pelo menos 1 sucesso OU todas foram puladas
        return hasAnySuccess || allSkipped;
      case 'success':
        return true;
      default:
        return true;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Aviso de dados salvos */}
      {showSavedNotice && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 animate-fade-in">
          <CheckCircle className="h-5 w-5" />
          <div>
            <p className="font-bold">✅ Progresso Restaurado!</p>
            <p className="text-sm">Seus dados anteriores foram carregados</p>
          </div>
          <button 
            onClick={() => setShowSavedNotice(false)}
            className="ml-4 hover:bg-green-600 rounded p-1"
          >
            ✕
          </button>
        </div>
      )}
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-white">
              Setup <span className="text-yellow-400">MonetizeMaster</span>
            </h1>
            <div className="flex items-center gap-4">
              {localStorage.getItem('monetizemaster_onboarding') && (
                <button
                  onClick={() => {
                    if (window.confirm('Deseja limpar todo o progresso salvo e começar do zero?')) {
                      localStorage.removeItem('monetizemaster_onboarding');
                      window.location.reload();
                    }
                  }}
                  className="text-xs bg-red-500/20 hover:bg-red-500/30 text-red-300 px-3 py-1 rounded-lg transition-all"
                  title="Limpar dados salvos"
                >
                  🗑️ Limpar Progresso
                </button>
              )}
              <span className="text-gray-400">
                Passo {currentStep + 1} de {steps.length}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 mb-4">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold relative ${
                  skippedSteps.includes(step.id) ? 'bg-orange-500 text-white' :
                  index < currentStep ? 'bg-green-500 text-white' :
                  index === currentStep ? 'bg-yellow-500 text-black' :
                  'bg-gray-600 text-gray-400'
                }`}>
                  {skippedSteps.includes(step.id) ? (
                    <span title="Etapa pulada">⏭️</span>
                  ) : index < currentStep ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    index + 1
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-12 h-1 ${
                    index < currentStep ? 'bg-green-500' : 'bg-gray-600'
                  }`} />
                )}
              </div>
            ))}
          </div>
          
          <div className="bg-gray-700 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-yellow-400 to-orange-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
          
          {/* Legenda */}
          <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded-full bg-green-500"></div>
              <span>Completo</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
              <span>Atual</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded-full bg-orange-500 flex items-center justify-center text-white">⏭</div>
              <span>Pulado</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded-full bg-gray-600"></div>
              <span>Pendente</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-8 mb-8">
          {renderCurrentStep()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <button
            onClick={prevStep}
            disabled={currentStep === 0}
            className="flex items-center gap-2 px-6 py-3 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="h-5 w-5" />
            Anterior
          </button>
          
          <div className="text-center">
            <p className="text-gray-400 text-sm">
              {steps[currentStep].description}
            </p>
          </div>
          
          {currentStep < steps.length - 1 ? (
            <button
              onClick={nextStep}
              disabled={!canProceed()}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-600 text-white rounded-lg hover:from-yellow-600 hover:to-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próximo
              <ArrowRight className="h-5 w-5" />
            </button>
          ) : (
            <button
              onClick={() => {
                if (onComplete) onComplete();
                navigate('/dashboard');
              }}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all"
            >
              Finalizar
              <CheckCircle className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingFlow;